import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { capitalize, mapValues } from 'lodash';

import type { GenericChartDataType } from '@/components/charts/charts.types';
import type { PercentageBarDataType } from '@/components/PercentageBarWidget/PercentageBarWidget.types';
import {
  CYBERSCORE_COLORS,
  RISK_COLORS
} from '@/constants/chartcolors.constants';
import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';
import userAtom from '@/store/user.store';

import { selectedMCSCAssessmentAtom } from '../Dashboard.store';
import type {
  MyCyberScoreFunctionDTO,
  PreferencesDTO
} from './DashboardMCSC.types';

export const cyberScoreFunctionsAtom = atom<GenericChartDataType[]>((get) => {
  const selectedMCSCAssessment = get(selectedMCSCAssessmentAtom);
  const scoreFunctions = selectedMCSCAssessment?.my_cyber_score?.functions;

  const data: GenericChartDataType[] = [];

  Object.entries(scoreFunctions || {}).forEach(([key, value]) => {
    data.push({
      name: capitalize(key),
      target: value?.target || 0,
      total: value?.score || 0
    });
  });

  return data;
});

export const cyberRiskFunctionsAtom = atom<GenericChartDataType[]>((get) => {
  const selectedMCSCAssessment = get(selectedMCSCAssessmentAtom);
  const riskFunctions = selectedMCSCAssessment?.my_cyber_risk?.functions;

  const data: GenericChartDataType[] = [];

  Object.entries(riskFunctions || {}).forEach(([key, value]) => {
    data.push({
      name: capitalize(key),
      total: value || 0
    });
  });

  return data;
});

export const cyberScoreCategoriesAtom = atom<GenericChartDataType[]>((get) => {
  const selectedMCSCAssessment = get(selectedMCSCAssessmentAtom);
  const scoreCategories = selectedMCSCAssessment?.my_cyber_score?.categories;

  const data: GenericChartDataType[] = [];

  Object.entries(scoreCategories || {}).forEach(([key, value]) => {
    data.push({
      name: capitalize(key),
      score: value?.score || 0,
      target: value?.target || 0
    });
  });

  return data;
});

export const cyberScoreRiskItemsAtom = atom((get) => {
  const selectedMCSCAssessment = get(selectedMCSCAssessmentAtom);
  const riskItems = selectedMCSCAssessment?.my_cyber_risk?.risk_items;
  const riskData: PercentageBarDataType[] = [];

  if (riskItems) {
    Object.keys(riskItems).forEach((key, index) => {
      riskData.push({
        name: capitalize(key),
        data: [riskItems[key]],
        color: RISK_COLORS[index]
      });
    });
  }

  return riskData;
});

export const cyberScoreFunctionsCompletionAtom = atom<PercentageBarDataType[]>(
  (get) => {
    const selectedMCSCAssessment = get(selectedMCSCAssessmentAtom);
    const scoreFunctions = selectedMCSCAssessment?.my_cyber_score?.functions;
    const functionsArr: PercentageBarDataType[] = [];

    const colors = Object.values(CYBERSCORE_COLORS);

    if (scoreFunctions) {
      mapValues(
        scoreFunctions,
        (scoreFunction: MyCyberScoreFunctionDTO, key: string) => {
          if (scoreFunction === null) return null;
          // For each score function, push the percent completion to the array
          functionsArr.push({
            name: capitalize(key),
            data: [scoreFunction.percent_completion ?? 0], // Use percent_completion for the data
            color: colors[Object.keys(functionsArr).length % colors.length], // Cycle through colors
            type: 'percent' // Specify type as percent for the widget
          });
          return null;
        }
      );
    }

    return functionsArr;
  }
);

export const useUpdatePreferences = () => {
  const [, setUser] = useAtom(userAtom);
  const handleError = useHandleError();

  return useMutation({
    mutationFn: (preferences: PreferencesDTO) => {
      return axiosClient.put('/users/preferences', preferences);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update user preferences.');
    },
    onSuccess: (response) => {
      // Update the user atom with the new preferences
      setUser((prevUser) => {
        if (!prevUser) return prevUser;
        return {
          ...prevUser,
          preferences: {
            ...response.data.preferences
          }
        };
      });
    }
  });
};
